import React, { useState, useEffect, useCallback, useRef } from 'react';
import { referralAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import AmbassadorBadge from './AmbassadorBadge';
import {
  Gift,
  Copy,
  CheckCircle,
  Clock,
  TrendingUp,
  Users,
  Loader2,
  ChevronDown,
  ChevronUp,
  Zap,
  ExternalLink,
  Sparkles,
  PiggyBank,
  ArrowUpRight,
  AlertCircle,
} from 'lucide-react';

/* ─────────────────────────────────────────────────────────────────
   STATUS BADGE
───────────────────────────────────────────────────────────────── */
const StatusBadge = ({ status }) => {
  const map = {
    pending_verification: { label: 'Reviewing',  cls: 'bg-amber-500/15 text-amber-400 border border-amber-500/30' },
    approved:             { label: 'Upcoming',   cls: 'bg-blue-500/15 text-blue-400 border border-blue-500/30' },
    paid:                 { label: 'Paid',        cls: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' },
    voided:               { label: 'Voided',      cls: 'bg-red-500/15 text-red-400 border border-red-500/30' },
    booked:               { label: 'Booked',      cls: 'bg-blue-500/15 text-blue-400 border border-blue-500/30' },
  };
  const { label, cls } = map[status] || { label: status, cls: 'bg-zinc-700 text-zinc-400 border border-zinc-600' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${cls}`}>
      {label}
    </span>
  );
};

/* ─────────────────────────────────────────────────────────────────
   REWARD TYPE BADGE  (EARNING / SAVING)
───────────────────────────────────────────────────────────────── */
const RewardTypeBadge = ({ type }) => {
  if (type === 'EARNING') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
        Earning
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest bg-sky-500/15 text-sky-400 border border-sky-500/30">
      Saving
    </span>
  );
};

/* ─────────────────────────────────────────────────────────────────
   TOTAL SAVINGS CARD  (headline counter)
───────────────────────────────────────────────────────────────── */
const TotalSavingsCard = ({ combinedGbp, totalEarnedGbp, totalSavedGbp, pendingEarnedGbp, isAmbassador }) => (
  <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1a2a0e] via-[#0f1f0a] to-[#0D1323] border border-emerald-500/25 p-6">
    {/* Bg glow */}
    <div className="absolute top-0 right-0 w-48 h-48 bg-emerald-500/8 rounded-full blur-3xl pointer-events-none" />
    <div className="absolute bottom-0 left-0 w-32 h-32 bg-sky-500/8 rounded-full blur-3xl pointer-events-none" />

    <div className="relative flex items-start justify-between">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <PiggyBank size={16} className="text-emerald-400" />
          <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500">
            Total Saved &amp; Earned
          </span>
          {isAmbassador && (
            <AmbassadorBadge size={18} glow={true} tooltip={true} />
          )}
        </div>
        <div className="text-4xl font-black text-white tabular-nums mb-1">
          £{combinedGbp?.toFixed(2) ?? '0.00'}
        </div>
        <p className="text-xs text-zinc-500">lifetime value from Ariv</p>
      </div>
      <Sparkles size={28} className="text-emerald-500/30 flex-shrink-0" />
    </div>

    {/* Split breakdown */}
    <div className="mt-4 pt-4 border-t border-white/5 grid grid-cols-3 gap-3">
      <div>
        <div className="text-sm font-black text-emerald-400 tabular-nums">
          £{totalEarnedGbp?.toFixed(2) ?? '0.00'}
        </div>
        <div className="text-[9px] text-zinc-600 uppercase tracking-widest mt-0.5">Earned</div>
      </div>
      <div>
        <div className="text-sm font-black text-sky-400 tabular-nums">
          £{totalSavedGbp?.toFixed(2) ?? '0.00'}
        </div>
        <div className="text-[9px] text-zinc-600 uppercase tracking-widest mt-0.5">Saved</div>
      </div>
      <div>
        <div className="text-sm font-black text-amber-400 tabular-nums">
          £{pendingEarnedGbp?.toFixed(2) ?? '0.00'}
        </div>
        <div className="text-[9px] text-zinc-600 uppercase tracking-widest mt-0.5">Verifying</div>
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   EARNINGS CARD
───────────────────────────────────────────────────────────────── */
const EarningsCard = ({ icon: Icon, label, value, sub, glowColor }) => (
  <div className={`relative bg-[#0D1323] border border-white/8 rounded-xl p-5 overflow-hidden`}>
    <div className={`absolute top-0 right-0 w-20 h-20 rounded-full blur-2xl opacity-20 ${glowColor}`} />
    <Icon size={18} className={`mb-3 ${glowColor.replace('bg-', 'text-')}`} />
    <div className="text-2xl font-black text-white mb-0.5 tabular-nums">{value}</div>
    <div className="text-xs font-semibold text-zinc-400 uppercase tracking-widest">{label}</div>
    {sub && <div className="text-[11px] text-zinc-600 mt-1">{sub}</div>}
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   COPY TOAST
───────────────────────────────────────────────────────────────── */
const CopyToast = ({ visible }) => (
  <div
    className={`fixed bottom-6 right-6 z-50 flex items-center gap-2.5 px-4 py-3 rounded-xl
      bg-[#0A0F1E] border border-emerald-500/50 shadow-2xl shadow-emerald-500/10
      transition-all duration-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}
  >
    <CheckCircle size={16} className="text-emerald-400" />
    <span className="text-sm font-semibold text-emerald-300">Link copied. Share it and start earning.</span>
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   REFERRAL CODE BOX
───────────────────────────────────────────────────────────────── */
const ReferralBox = ({ refCode, shareLink, onCopy }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareLink || refCode);
      setCopied(true);
      onCopy?.();
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Failed to copy!', err);
    }
  };

  return (
    <div className="bg-[#0D1323] p-4 rounded-xl border border-zinc-800">
      <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-bold mb-2">Your Unique Referral Link</p>
      <div className="flex items-center gap-2 bg-[#0A0F1E] p-3 rounded-lg border border-zinc-700">
        <code className="text-blue-400 flex-1 truncate text-xs sm:text-sm select-all">
          {shareLink || refCode}
        </code>
        <button
          onClick={handleCopy}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-md transition-all flex-shrink-0 ${
            copied
              ? 'bg-emerald-500/20 text-emerald-400'
              : 'bg-blue-600 text-white hover:bg-blue-500'
          }`}
        >
          {copied ? <CheckCircle size={14} /> : <Copy size={14} />}
          <span className="text-xs font-semibold">{copied ? 'Copied!' : 'Copy'}</span>
        </button>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   SOCIAL PREVIEW CARD
───────────────────────────────────────────────────────────────── */
const SocialPreview = () => (
  <div className="mt-5 border border-zinc-800 rounded-xl overflow-hidden bg-[#0A0F1E]">
    <div className="bg-zinc-900/50 px-4 py-2 border-b border-zinc-800 flex justify-between items-center">
      <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Social Preview</span>
      <span className="text-[10px] text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded-full font-semibold">Live Link</span>
    </div>
    <div className="p-4">
      <div className="rounded-lg border border-zinc-700 overflow-hidden bg-[#161B22] shadow-2xl max-w-sm mx-auto">
        {/* Mock OG image */}
        <div className="aspect-video bg-gradient-to-br from-[#1F3A68] to-[#0D1323] flex flex-col items-center justify-center relative select-none">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-400 to-transparent" />
          <div className="absolute top-3 left-3 w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
            <span className="text-white font-black text-xs">Ariv</span>
          </div>
          <span className="text-white font-black text-xl tracking-tighter text-center px-4 relative z-10">
            ARIV
          </span>
          <span className="text-blue-300 text-xs mt-1 relative z-10">Everything arrives.</span>
          <div className="absolute bottom-3 right-3 bg-amber-400/20 border border-amber-400/40 rounded-full px-2 py-0.5">
            <span className="text-amber-300 text-[10px] font-bold">Early Member 🗝️</span>
          </div>
        </div>
        {/* Mock metadata */}
        <div className="p-3 space-y-1">
          <p className="text-[10px] text-zinc-500 uppercase tracking-widest">ariv.co.uk</p>
          <p className="text-sm font-semibold text-zinc-200">You're Invited to Ariv 🗝️</p>
          <p className="text-xs text-zinc-400 line-clamp-2">
            Join the UK super app for stays, rides, food and services. Earn a slice of every platform fee from anyone you invite.
          </p>
        </div>
      </div>
      <p className="text-[11px] text-zinc-600 text-center mt-3 italic">
        This is what your friends will see in WhatsApp, iMessage &amp; LinkedIn when you share your link.
      </p>
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   SHARE COPY PANEL (platform-specific viral scripts)
───────────────────────────────────────────────────────────────── */
// Share copy is kept factual on purpose (DMCC Act 2024 / CAP Code). Two
// claims were removed 2026-10-05: a "hurry, only N places left" line that
// was a fixed sentence, not a real count (invented scarcity), and "you earn
// from every transaction", which isn't true — a referrer's share is capped
// at what Ariv itself charges, so a founding host's 0% bookings earn nothing.
// backend/tests/test_referral_programme_alignment.py guards both.
const SHARE_HOW_IT_WORKS =
  `Invite a host, driver, restaurant, delivery rider or service provider (cleaner, handyman etc) and you get a share of what Ariv earns from their bookings and jobs. Invite a guest and you earn a little on their rides and food orders. It comes out of Ariv's cut, so it costs them nothing extra.`;

const ShareCopyPanel = ({ code, shareLink }) => {
  const [activeTab, setActiveTab] = useState('whatsapp');
  const [copied, setCopied] = useState(false);

  const copies = {
    whatsapp: {
      label: '💬 WhatsApp',
      icon: '💬',
      hint: 'Best for close friends and family, high trust, high conversion',
      text: `I just joined Ariv, a new UK platform for short stays, rides, food and local services. When people you invite use it, you get a share of what Ariv earns from them, for as long as they keep using it.

${SHARE_HOW_IT_WORKS}

Sign up with my link 👇

${shareLink}`,
    },
    instagram: {
      label: '📸 Instagram',
      icon: '📸',
      hint: 'Best for lifestyle appeal, paste your link in your bio or story sticker',
      text: `Just joined Ariv, the new UK app for short stays, rides, food and local services. Invite people and you get a share of what Ariv earns from them, for as long as they use it.

${SHARE_HOW_IT_WORKS}

Join with my link 👇

${shareLink}`,
    },
    twitter: {
      label: '✕ X / LinkedIn',
      icon: '✕',
      hint: 'Best for PropTech and property networking',
      text: `Ariv is a new UK platform for short stays, rides, food and local services, and it shares its revenue with the people who grow it. Invite someone and you get a share of what Ariv earns from them, for as long as they use it.

${SHARE_HOW_IT_WORKS}

Join with my link 👇

${shareLink}

#PropTech #UK`,
    },
  };

  const active = copies[activeTab];

  const handleCopy = async () => {
    await navigator.clipboard.writeText(active.text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(copies.whatsapp.text)}`;
  const twitterUrl  = `https://x.com/intent/tweet?text=${encodeURIComponent(copies.twitter.text)}`;

  return (
    <div className="bg-[#0D1323] border border-white/8 rounded-xl overflow-hidden">
      {/* Tab bar */}
      <div className="flex border-b border-white/8">
        {Object.entries(copies).map(([key, val]) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex-1 px-3 py-2.5 text-xs font-semibold transition-all ${
              activeTab === key
                ? 'bg-white/6 text-white border-b-2 border-blue-500'
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
          >
            {val.icon} {key.charAt(0).toUpperCase() + key.slice(1)}
          </button>
        ))}
      </div>

      {/* Copy script */}
      <div className="p-4">
        <p className="text-[10px] text-zinc-600 italic mb-2">{active.hint}</p>
        <div className="bg-[#0A0F1E] border border-zinc-800 rounded-lg p-3">
          <pre className="text-xs text-zinc-300 whitespace-pre-wrap font-sans leading-relaxed">
            {active.text}
          </pre>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2 mt-3">
          <button
            onClick={handleCopy}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              copied
                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                : 'bg-white/8 text-zinc-300 hover:bg-white/12 border border-white/10'
            }`}
          >
            {copied ? <CheckCircle size={12} /> : <Copy size={12} />}
            {copied ? 'Copied!' : 'Copy message'}
          </button>

          {activeTab === 'whatsapp' && (
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#25D366] text-white hover:opacity-90 transition-all"
            >
              <ExternalLink size={12} />
              Open WhatsApp
            </a>
          )}
          {activeTab === 'twitter' && (
            <a
              href={twitterUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-black text-white border border-zinc-700 hover:opacity-90 transition-all"
            >
              <ExternalLink size={12} />
              Open X
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────────── */
const ReferralDashboard = () => {
  const { getAuthToken } = useAuth();

  const [codeData,      setCodeData]      = useState(null);
  const [codeError,     setCodeError]     = useState(null);   // surface failures from /my-code
  const [stats,         setStats]         = useState(null);
  const [history,       setHistory]       = useState([]);
  const [ledger,        setLedger]        = useState(null);   // reward ledger
  const [loading,       setLoading]       = useState(true);
  const [showToast,     setShowToast]     = useState(false);
  const [showAll,       setShowAll]       = useState(false);
  const [showAllLedger, setShowAllLedger] = useState(false);
  const [capReached,    setCapReached]    = useState(false);
  const toastTimer = useRef(null);

  /* ── Load data ── */
  // Each sub-call is wrapped in its own catch so a single failed call (e.g.
  // /my-code) doesn't take the entire dashboard down. The code section then
  // renders its own fallback with a retry button.
  const load = useCallback(async () => {
    setLoading(true);
    setCodeError(null);
    try {
      const token = getAuthToken();
      const [codeRes, statsRes, histRes, ledgerRes] = await Promise.all([
        referralAPI.getMyCode(token).catch(err => {
          console.error('ReferralDashboard /my-code error', err);
          setCodeError(
            err?.response?.data?.detail
            || err?.message
            || 'Could not load your referral link.'
          );
          return null;
        }),
        referralAPI.getStats(token).catch(() => null),
        referralAPI.getHistory(token, 50).catch(() => null),
        referralAPI.getRewardLedger(token, 100).catch(() => null),
      ]);
      setCodeData(codeRes);
      // `active` is false while new referrals can't earn: the programme is
      // paused or the cap is full. capReached alone missed the pause.
      setCapReached(codeRes?.active === false || !!codeRes?.capReached);
      setStats(statsRes);
      setHistory(histRes?.payouts || []);
      setLedger(ledgerRes);
    } finally {
      setLoading(false);
    }
  }, [getAuthToken]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  /* ── Show copy toast ── */
  const handleCopy = () => {
    setShowToast(true);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setShowToast(false), 3500);
  };

  const fmt = (pence) => `£${((pence || 0) / 100).toFixed(2)}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 bg-[#080E1C] min-h-[50vh]">
        <Loader2 size={28} className="animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="bg-[#080E1C] min-h-screen text-white">
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">

        {/* ── Hero ── */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1F3A68] via-[#162d52] to-[#0D1323] border border-white/10 p-6">
          {/* Background glow */}
          <div className="absolute top-0 right-0 w-56 h-56 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-40 h-40 bg-indigo-500/10 rounded-full blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <div className="w-7 h-7 rounded-lg bg-amber-400/20 border border-amber-400/40 flex items-center justify-center">
                <span className="text-amber-300 text-sm">🗝️</span>
              </div>
              {!capReached && (
                <div className="flex items-center gap-1.5 bg-amber-400/10 border border-amber-400/30 rounded-full px-2.5 py-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                  <span className="text-amber-300 text-[10px] font-bold uppercase tracking-widest">Early Member Programme</span>
                </div>
              )}
              {/* Ambassador badge — shown only when isAmbassador */}
              {codeData?.isAmbassador && (
                <AmbassadorBadge size={22} glow={true} tooltip={true} />
              )}
            </div>
            <h1 className="text-xl font-black leading-tight mb-2">
              Earn every time the people you invite use Ariv
            </h1>
            <p className="text-blue-300 text-sm leading-relaxed max-w-lg">
              Share your code. When someone joins through your link, you earn a small slice of every booking, ride, order, or job they bring through Ariv. The amount depends on the kind of user they are. They pay nothing extra; it comes out of our cut.
            </p>
          </div>
        </div>

        {/* ── Earnings Grid ── */}
        <div className="grid grid-cols-3 gap-3">
          <EarningsCard
            icon={CheckCircle}
            label="Total Earned"
            value={fmt(stats?.totalPaidPence)}
            sub={`${stats?.totalPaid || 0} paid referral${stats?.totalPaid !== 1 ? 's' : ''}`}
            glowColor="bg-emerald-500"
          />
          <EarningsCard
            icon={Clock}
            label="Reviewing"
            value={fmt(stats?.pendingVerificationPence)}
            sub="Awaiting approval"
            glowColor="bg-amber-500"
          />
          <EarningsCard
            icon={TrendingUp}
            label="Upcoming"
            value={fmt(stats?.approvedPence)}
            sub="Approved, not yet paid"
            glowColor="bg-blue-500"
          />
        </div>

        {/* ── Referral Code Section ── */}
        {!capReached && codeData?.code ? (
          <div className="space-y-4">

            {/* Code box */}
            <ReferralBox
              refCode={codeData.code}
              shareLink={codeData.shareLink || codeData.referralUrl}
              onCopy={handleCopy}
            />

            {/* Plain English explainer — actual rates from services/referral_split.py */}
            <div className="flex items-start gap-2.5 bg-amber-500/8 border border-amber-500/20 rounded-xl px-4 py-3">
              <Zap size={14} className="text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-300 leading-relaxed">
                <strong>How much you earn:</strong> 3% of the accommodation price on each booking a host you referred takes; 2% of each ride, restaurant order or service job from drivers, restaurants and service providers; 1% of each delivery fee from delivery riders; and 0.5% wallet credit on the rides and food orders of guests you refer. Your share is never more than Ariv's own fee on that transaction, so you earn nothing while a founding host is in their free first 90 days. Paid for as long as they keep using Ariv.
              </p>
            </div>

            {/* Social preview */}
            <SocialPreview />

            {/* Note below preview */}
            <p className="text-[11px] text-zinc-600 text-center italic px-2">
              Your share is paid for as long as the people you bring in keep using Ariv.
              There's no time limit. Keep sharing to grow what you earn each month.
            </p>

            {/* Platform share copy */}
            <div>
              <p className="text-[10px] text-zinc-500 uppercase tracking-widest font-bold mb-3">
                Ready-to-send copy for each platform
              </p>
              <ShareCopyPanel
                code={codeData.code}
                shareLink={codeData.shareLink || codeData.referralUrl}
              />
            </div>

          </div>
        ) : capReached ? (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 text-center">
            <p className="text-zinc-500 text-sm">
              New referrals aren't earning rewards right now. People you've already referred still earn you your share. Check back soon.
            </p>
          </div>
        ) : (
          // codeData missing or code field empty — always show something so the
          // user has a way forward. Previously this branch rendered null, which
          // is how the referral link could silently disappear on a fresh account.
          <div className="bg-zinc-900 border border-amber-500/30 rounded-xl p-5 text-center space-y-3">
            <p className="text-amber-300 text-sm font-semibold">
              We couldn't load your referral link
            </p>
            <p className="text-zinc-400 text-xs leading-relaxed">
              {codeError
                ? `Reason: ${codeError}`
                : 'This sometimes happens on a brand new account before the link has been generated. Hit the button below and we will generate it for you.'}
            </p>
            <button
              onClick={load}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-500 transition"
            >
              Generate my link
            </button>
          </div>
        )}

        {/* ── Reward History Ledger ── */}
        {/* Total Savings headline card */}
        {(ledger?.combinedGbp > 0 || ledger?.totalEarnedGbp > 0) && (
          <TotalSavingsCard
            combinedGbp={ledger.combinedGbp}
            totalEarnedGbp={ledger.totalEarnedGbp}
            totalSavedGbp={ledger.totalSavedGbp}
            pendingEarnedGbp={ledger.pendingEarnedGbp}
            isAmbassador={!!codeData?.isAmbassador}
          />
        )}

        {/* Bank-statement ledger table */}
        <div className="bg-[#0D1323] border border-white/8 rounded-xl overflow-hidden">
          <div className="px-5 py-3.5 border-b border-white/8 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ArrowUpRight size={14} className="text-zinc-500" />
              <span className="text-xs font-bold uppercase tracking-widest text-zinc-400">Reward History</span>
            </div>
            {ledger?.total > 0 && (
              <span className="text-[10px] text-zinc-600 bg-white/5 px-2 py-0.5 rounded-full">
                {ledger.total} entries
              </span>
            )}
          </div>

          {(!ledger || ledger.rows?.length === 0) ? (
            <div className="py-12 text-center">
              <Gift size={36} className="text-zinc-800 mx-auto mb-3" />
              <p className="text-zinc-600 text-sm font-medium">No rewards yet</p>
              <p className="text-zinc-700 text-xs mt-1">Share your code or book a stay to unlock earnings &amp; savings</p>
            </div>
          ) : (
            <>
              {/* Column headers */}
              <div className="grid grid-cols-12 gap-2 px-5 py-2 bg-white/3 border-b border-white/5">
                {[
                  { label: 'Date',     cols: 'col-span-2' },
                  { label: 'Activity', cols: 'col-span-5' },
                  { label: 'Type',     cols: 'col-span-2' },
                  { label: 'Amount',   cols: 'col-span-3 text-right' },
                ].map(h => (
                  <span key={h.label} className={`text-[9px] font-black uppercase tracking-widest text-zinc-600 ${h.cols}`}>
                    {h.label}
                  </span>
                ))}
              </div>

              {/* Rows */}
              {(showAllLedger ? ledger.rows : ledger.rows.slice(0, 8)).map((row, i) => {
                const isPending = row.pending;
                const isVoided  = row.voided;
                const rowDate   = row.date ? new Date(row.date) : null;

                return (
                  <div
                    key={row.id || i}
                    className={`grid grid-cols-12 gap-2 px-5 py-3 border-b border-white/4
                      hover:bg-white/3 transition-colors last:border-b-0
                      ${isPending ? 'opacity-75' : ''}`}
                  >
                    {/* Date */}
                    <div className="col-span-2">
                      <span className={`text-[10px] tabular-nums ${isPending ? 'text-zinc-600 italic' : 'text-zinc-500'}`}>
                        {rowDate
                          ? rowDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })
                          : '—'}
                      </span>
                    </div>

                    {/* Activity */}
                    <div className="col-span-5 flex items-center gap-1.5 min-w-0">
                      {isPending && (
                        <AlertCircle size={10} className="text-amber-400 flex-shrink-0 animate-pulse" />
                      )}
                      <span className={`text-xs truncate ${isPending ? 'text-zinc-500 italic' : 'text-zinc-300'}`}>
                        {row.activity}
                      </span>
                    </div>

                    {/* Type badge */}
                    <div className="col-span-2 flex items-center">
                      {isPending
                        ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest bg-amber-500/10 text-amber-400 border border-amber-500/20">Verifying</span>
                        : <RewardTypeBadge type={row.type} />
                      }
                    </div>

                    {/* Amount */}
                    <div className="col-span-3 text-right">
                      {row.type === 'SAVING' ? (
                        <div>
                          <span className="text-[10px] text-zinc-600 line-through mr-1 tabular-nums">
                            £{row.amountGbp?.toFixed(2)}
                          </span>
                          <span className="text-xs font-bold text-sky-400 tabular-nums">
                            £0.00
                          </span>
                        </div>
                      ) : (
                        <span className={`text-xs font-bold tabular-nums ${
                          isVoided    ? 'text-red-400 line-through' :
                          isPending   ? 'text-amber-400' :
                          row.positive ? 'text-emerald-400' :
                          'text-zinc-300'
                        }`}>
                          {row.positive && !isVoided ? '+' : ''}£{row.amountGbp?.toFixed(2) ?? '0.00'}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Show all / less toggle */}
              {ledger.rows.length > 8 && (
                <button
                  onClick={() => setShowAllLedger(v => !v)}
                  className="w-full flex items-center justify-center gap-1.5 py-3 text-xs text-zinc-500 font-semibold hover:bg-white/3 transition-colors border-t border-white/5"
                >
                  {showAllLedger
                    ? <><ChevronUp size={12} /> Show less</>
                    : <><ChevronDown size={12} /> Show all {ledger.rows.length} entries</>
                  }
                </button>
              )}
            </>
          )}
        </div>

        {/* ── Legacy Referral History (peer list — kept for quick reference) ── */}
        {history.length > 0 && (
        <div className="bg-[#0D1323] border border-white/8 rounded-xl overflow-hidden">
          <div className="px-5 py-3.5 border-b border-white/8 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users size={14} className="text-zinc-500" />
              <span className="text-xs font-bold uppercase tracking-widest text-zinc-400">Referral History</span>
            </div>
            {stats?.totalReferrals > 0 && (
              <span className="text-[10px] text-zinc-600 bg-white/5 px-2 py-0.5 rounded-full">
                {stats.totalReferrals} total
              </span>
            )}
          </div>

              {/* Header */}
              <div className="grid grid-cols-4 gap-3 px-5 py-2 bg-white/3 border-b border-white/5">
                {['User', 'Service', 'Status', 'Your Cut'].map((h, i) => (
                  <span key={h} className={`text-[9px] font-black uppercase tracking-widest text-zinc-600 ${i === 3 ? 'text-right' : ''}`}>
                    {h}
                  </span>
                ))}
              </div>

              {/* Rows */}
              {(showAll ? history : history.slice(0, 5)).map((row, i) => (
                <div
                  key={row._id || i}
                  className="grid grid-cols-4 gap-3 px-5 py-3 border-b border-white/4 hover:bg-white/3 transition-colors last:border-b-0"
                >
                  <span className="text-xs text-zinc-300 font-medium truncate">
                    {row.newUserFirstName || row.newUserName?.split(' ')[0] || '—'}
                  </span>
                  <span className="text-xs text-zinc-500 capitalize truncate">
                    {row.serviceType || row.bookingType || 'Stay'}
                  </span>
                  <div><StatusBadge status={row.status} /></div>
                  <span className={`text-xs font-bold tabular-nums text-right ${
                    row.status === 'paid'   ? 'text-emerald-400' :
                    row.status === 'voided' ? 'text-red-400 line-through' :
                    'text-zinc-300'
                  }`}>
                    {fmt(row.payoutAmountPence)}
                  </span>
                </div>
              ))}

              {history.length > 5 && (
                <button
                  onClick={() => setShowAll(v => !v)}
                  className="w-full flex items-center justify-center gap-1.5 py-3 text-xs text-zinc-500 font-semibold hover:bg-white/3 transition-colors border-t border-white/5"
                >
                  {showAll
                    ? <><ChevronUp size={12} /> Show less</>
                    : <><ChevronDown size={12} /> Show all {history.length} referrals</>
                  }
                </button>
              )}
        </div>
        )}

        {/* ── How It Works ── */}
        <div className="bg-[#0D1323] border border-white/8 rounded-xl p-5">
          <p className="text-[10px] font-black uppercase tracking-widest text-zinc-600 mb-4">How it works</p>
          <div className="space-y-3">
            {[
              { n: '1', t: 'Share your unique code with friends, colleagues, or post it on social media.' },
              { n: '2', t: 'They sign up using your link and complete their first booking or order.' },
              { n: '3', t: "You earn a share each time they use Ariv: 3% from hosts, 2% from drivers / restaurants / service providers, 1% from delivery riders, never more than Ariv's own fee. Our team checks and approves each payout." },
            ].map(({ n, t }) => (
              <div key={n} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-blue-500/20 border border-blue-500/30 text-blue-400 text-[10px] font-black flex items-center justify-center flex-shrink-0 mt-0.5">
                  {n}
                </div>
                <p className="text-xs text-zinc-400 leading-relaxed">{t}</p>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-zinc-700 mt-4 leading-relaxed">
            Every payout is checked for fraud and approved by our team before it's released. Ariv reserves the right to void rewards for suspected abuse.
            Full rules are in our Terms of Service, section 10.6 (Referral Programme).
          </p>
          <div className="mt-3 pt-3 border-t border-white/5 flex items-start gap-2">
            <AlertCircle size={11} className="text-zinc-700 flex-shrink-0 mt-0.5" />
            <p className="text-[10px] text-zinc-700 leading-relaxed">
              <strong className="text-zinc-600">Reward missing?</strong>{' '}
              If you believe a saving or earning wasn't applied correctly, visit the{' '}
              <span className="text-zinc-500">Help Centre</span>{' '}
              and our team can manually review and credit your account.
            </p>
          </div>
        </div>

      </div>

      {/* Global copy toast */}
      <CopyToast visible={showToast} />
    </div>
  );
};

export default ReferralDashboard;
